import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { browserHistory } from 'react-router';
import FooterButtons from '@components/Button/FooterButtons';
import DocConsultSuccessModal from '@components/DocConsultSuccessModal';
import PatientForm from '@components/PatientForm/PatientForm';
import UploadReport from '@components/UploadReport/UploadReport';
import { getDoctorDisplayName, parseDisplayDate, formatTime, getSlotHour } from '@utils/docConsultUtils';
import get from 'lodash/get';
import { uploadImagesToS3 } from '../../utils/helper';
import { age as validateAge, contact as validateContact, name as validateName } from '../../utils/validations';
import * as actions from '../../actions';
import * as Constants from '../../constants';
import { IMAGE_URL_PREFIX } from '../../constants';
import './SlotPatientPage.scss';
import ARROW_DOWN from '../../images/arrow_down.svg';

const staticText = {
  initialsPlaceholder: '?',
  timePrefix: 'Time: ',
  yearsExp: 'years Exp',
  speaks: 'Speaks: ',
  loadingSlots: 'Loading slots...',
  noSlots: 'No slots available.',
  viewMore: 'View More Slots',
  viewLess: 'View Less Slots',
  consultModeTitle: 'Choose your mode of consult',
  privacyPolicyLink: 'Privacy Policy',
  backBtn: 'Back to Doctor Selection',
  placingOrder: 'Placing Order...',
  confirmBooking: 'Confirm Booking',
  cancelBtn: 'Cancel Order',
};


const CONSULT_TYPES = [
  {
    id: 'AUDIO',
    name: 'Audio Call',
    iconSelected: `${IMAGE_URL_PREFIX}icon-docConsult-audio.svg`,
    iconDisabled: `${IMAGE_URL_PREFIX}icon-docConsult-audio-disabled.svg`,
  },
  {
    id: 'VIDEO',
    name: 'Video Call',
    iconSelected: `${IMAGE_URL_PREFIX}icon-docConsult-video.svg`,
    iconDisabled: `${IMAGE_URL_PREFIX}icon-docConsult-video-disabled.svg`,
  },
];

const TIME_FILTERS = [
  { label: 'Morning', startHour: 0, endHour: 12 },
  { label: 'Afternoon', startHour: 12, endHour: 16 },
  { label: 'Evening', startHour: 16, endHour: 20 },
  { label: 'Night', startHour: 20, endHour: 24 },
];

const OMS_ENABLED = true;

class SlotPatientPage extends Component {
  constructor(props) {
    super(props);
    this.patientFormRef = null;
    this.uploadReportRef = null;
    this.state = {
      showAllSlots: false,
      selectedFilter: null,
      orderLoading: false,
      orderSuccess: false,
      orderId: null,
      orderError: '',
    };
  }

  fetchBifurcation = (consultationType) => {
    const { selectedDoctor } = this.props.docConsult;
    if (!selectedDoctor || !selectedDoctor.id) return;
    this.props.actions.fetchDocConsultBifurcation({
      removeAllCharges: true,
      walletOptedIn: 0,
      refundOptIn: 0,
      consultationType: consultationType.toLowerCase(),
      checkout: 0,
      doctorId: selectedDoctor.id,
      price: {
        consultationFee: String(selectedDoctor.consultationFees || 0),
        discountedFee: String(selectedDoctor.consultationFees || 0),
      },
      vendorId: selectedDoctor.vendorId,
      isConsultCorporateWalletOptedIn: 0,
    });
  }

  componentDidMount() {
    const { selectedDoctor, consultMode } = this.props.docConsult;
    if (selectedDoctor && selectedDoctor.id) {
      this.props.actions.fetchDocConsultSlots(selectedDoctor.id);
      this.fetchBifurcation(consultMode || 'AUDIO');
    }
    this.props.actions.changeHeaderState(Constants.DOC_CONSULT_SLOT_PATIENT);
  }

  handleDateChange = (date) => {
    this.props.actions.setSelectedDate(date);
    this.setState({ showAllSlots: false, selectedFilter: null });
  }

  handleSlotChange = (slot) => {
    this.props.actions.setDocConsultSlot(slot);
  }

  handleFilterChange = (label) => {
    this.setState(prev => ({
      selectedFilter: prev.selectedFilter === label ? null : label,
      showAllSlots: false,
    }));
  }

  handleConsultMode = (id) => {
    this.props.actions.setConsultMode(id);
    // pricing could differ between audio and video
    this.fetchBifurcation(id);
  }

  handlePatientChange = (patientData) => {
    this.props.actions.setPatientDetails(patientData);
  }

  handleProceed = async () => {
    if (this.patientFormRef && !this.patientFormRef.validateAll()) return;

    const { selectedDoctor, selectedSlot, selectedDate, consultMode, appliedPromoCode, patientDetails } = this.props.docConsult;
    const customerName = get(this.props.customer, 'customerDetails.data.profile.name', '');
    const customerContact = get(this.props.customer, 'customerDetails.data.profile.mobileNumber', '');

    this.setState({ orderLoading: true, orderError: '' });

    // Upload any report files to S3 before creating the order
    let images = [], pdfFiles = [];
    const uploadedFiles = this.uploadReportRef ? this.uploadReportRef.getFiles() : [];

    if (uploadedFiles.length > 0) {
      const imgFiles = uploadedFiles.filter(file => !file.isPdf);
      const pdfFilesList = uploadedFiles.filter(file => file.isPdf);
      try {
        const res = await this.props.actions.fetchS3UploadLinks(imgFiles.length, pdfFilesList.length, OMS_ENABLED);
        const s3Links = (res && res.data) || (res && res.urls) || [];
        await uploadImagesToS3(s3Links, uploadedFiles);
        s3Links.forEach(link => {
          if (link.type === Constants.FILE_TYPE.pdf) pdfFiles.push(link.key);
          else images.push(link.key);
        });
      } catch (err) {
        this.setState({ orderLoading: false, orderError: 'Failed to upload files. Please try again.' });
        return;
      }
    }

    const displayName = getDoctorDisplayName(selectedDoctor).replace(/^Dr /, '');

    const body = {
      removeAllCharges: true,
      walletOptedIn: 0,
      isConsultCorporateWalletOptedIn: 0,
      refundOptIn: 0,
      consultation: {
        appointment: {
          appointmentDate: selectedDate,
          slotId: selectedSlot.id,
          slotStartTime: selectedSlot.start,
          slotEndTime: selectedSlot.end || '',
          consultationType: consultMode,
        },
        doctor: {
          doctorId: String(selectedDoctor.id),
          doctorName: displayName,
          specialization: selectedDoctor.specialization || '',
          doctorType: selectedDoctor.doctorType || '',
          expertiseIn: selectedDoctor.expertiseIn ? selectedDoctor.expertiseIn.split(',')[0].trim() : '',
          consultationTiming: null,
        },
        vendorId: selectedDoctor.vendorId,
        vendorName: selectedDoctor.vendorName || 'MeraDoc',
        associatedDiagOrderId: '',
        associatedDiagPatientId: '',
        associatedDiagReportId: '',
        patient: {
          name: patientDetails.name || customerName,
          gender: patientDetails.gender || 'Male',
          contact: patientDetails.contact || customerContact,
          age: patientDetails.age ? String(patientDetails.age) : null,
        },
        isFreeFollowUp: false,
        pastConsultationOrderId: '',
        reason: '',
        symptoms: [],
      },
      images,
      pdfFiles,
      ...(appliedPromoCode && { promoCode: appliedPromoCode }),
    };

    this.props.actions.createDocConsultOrder(body).then(res => {
      const orderId = (res && (res.orderId || (res.data && res.data.orderId))) || null;
      if (orderId) {
        this.setState({ orderLoading: false, orderSuccess: true, orderId });
      } else {
        const msg = (res && res.message) || (res && res.error && res.error.message) || 'Failed to place order. Please try again.';
        this.setState({ orderLoading: false, orderError: msg });
      }
    }).catch(() => {
      this.setState({ orderLoading: false, orderError: 'Failed to place order. Please try again.' });
    });
  }

  isValid() {
    const { selectedSlot, patientDetails } = this.props.docConsult;
    const { name, contact, age } = patientDetails;
    return !!(
      selectedSlot &&
      validateName(name) &&
      contact && validateContact(contact) &&
      validateAge(age)
    );
  }

  renderDoctorCard() {
    const { selectedDoctor, selectedSlot, selectedDate } = this.props.docConsult;
    if (!selectedDoctor) return null;
    const { firstName, specialization, yearsOfExperience, language, profileUrl } = selectedDoctor;
    const displayName = getDoctorDisplayName(selectedDoctor);
    const initials = firstName ? firstName[0].toUpperCase() : staticText.initialsPlaceholder;
    const langs = Array.isArray(language) ? language.join(', ') : language;
    const slotTime = selectedSlot ? formatTime(selectedSlot.start) : null;
    const slotDateLabel = selectedDate ? parseDisplayDate(selectedDate).displayDate : null;

    return (
      <div className="csCard">
        {profileUrl ? (
          <img src={profileUrl} alt={displayName} className="csCardImg" />
        ) : (
          <div className="csCardPlaceholder">{initials}</div>
        )}
        <div className="csCardDetails">
          <h3 className="csCardName">{displayName}</h3>
          {specialization && <p className="csCardType">{specialization}</p>}
          {slotTime && slotDateLabel && (
            <p className="csCardExp">{staticText.timePrefix}{slotTime}, {slotDateLabel}</p>
          )}
          {yearsOfExperience && <p className="csCardExp">{yearsOfExperience} {staticText.yearsExp}</p>}
          {langs && <p className="csCardLang">{staticText.speaks}{langs}</p>}
        </div>
      </div>
    );
  }

  renderSlotSection() {
    const { slotData, selectedDate, selectedSlot, slotLoading, slotsTitle } = this.props.docConsult;
    const { showAllSlots, selectedFilter } = this.state;

    const displayDates = (slotData || []).map(dateItem => parseDisplayDate(dateItem.date));
    const activeDateStr = selectedDate || (displayDates.length > 0 ? displayDates[0].date : '');
    const activeDateObj = (slotData || []).find(dateItem => dateItem.date === activeDateStr);
    const allSlots = (activeDateObj && activeDateObj.slots) || [];

    // Available time-of-day filters for this date
    const availableFilters = TIME_FILTERS.filter(timeFilter =>
      allSlots.some(slot => {
        const h = getSlotHour(slot);
        return h >= timeFilter.startHour && h < timeFilter.endHour;
      })
    );

    // Apply filter
    const filteredSlots = selectedFilter
      ? allSlots.filter(slot => {
          const h = getSlotHour(slot);
          const timeFilter = TIME_FILTERS.find(filter => filter.label === selectedFilter);
          return timeFilter && h >= timeFilter.startHour && h < timeFilter.endHour;
        })
      : allSlots;

    const visibleSlots = showAllSlots || filteredSlots.length <= Constants.SLOT_DISPLAY_CLAMP
      ? filteredSlots
      : filteredSlots.slice(0, Constants.SLOT_DISPLAY_CLAMP);

    return (
      <div className="csSlotSection">
        {slotsTitle && <h3 className="csSlotTitle">{slotsTitle}</h3>}

        {slotLoading ? (
          <p className="csSlotLoading">{staticText.loadingSlots}</p>
        ) : (
          <div>
            {/* Date tabs */}
            <div className="csDateRow">
              {displayDates.map(({ date, displayDay, displayDate }, idx) => (
                <div key={date} className="csDateItemWrapper">
                  <div
                    className={'csDateItem' + (date === activeDateStr ? ' csDateItemActive' : '')}
                    onClick={() => this.handleDateChange(date)}
                  >
                    <p className="csDateDay">{displayDay}</p>
                    <span className="csDateNum">{displayDate}</span>
                  </div>
                  {idx < displayDates.length - 1 && <div className="csDateDivider" />}
                </div>
              ))}
            </div>

            {/* Time-of-day filter chips */}
            {availableFilters.length > 1 && (
              <div className="csFilterRow">
                {availableFilters.map(f => (
                  <div
                    key={f.label}
                    className={'csFilterChip' + (selectedFilter === f.label ? ' csFilterChipActive' : '')}
                    onClick={() => this.handleFilterChange(f.label)}
                  >
                    <span className="csFilterText">{f.label}</span>
                  </div>
                ))}
              </div>
            )}

            {/* Slot grid */}
            {filteredSlots.length === 0 ? (
              <p className="csNoSlots">{staticText.noSlots}</p>
            ) : (
              <div>
                <div className="csSlotGrid">
                  {visibleSlots.map(slot => (
                    <div
                      key={slot.id}
                      className={'csSlotChip' + (selectedSlot && selectedSlot.id === slot.id ? ' csSlotChipActive' : '')}
                      onClick={() => this.handleSlotChange(slot)}
                    >
                      <p className="csSlotTime">{formatTime(slot.start)}</p>
                    </div>
                  ))}
                </div>
                {filteredSlots.length > Constants.SLOT_DISPLAY_CLAMP && (
                  <div
                    className="consultViewMore"
                    onClick={() => this.setState({ showAllSlots: !showAllSlots })}
                  >
                    {showAllSlots ? staticText.viewLess : staticText.viewMore}
                    <img
                      src={ARROW_DOWN}
                      alt="arrow"
                      className={'consultViewMoreArrow' + (showAllSlots ? ' consultViewMoreArrowUp' : '')}
                      width="14"
                      height="14"
                    />
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    );
  }

  renderConsultMode() {
    const { consultMode } = this.props.docConsult;

    return (
      <div className="csConsultMode">
        <div className="csConsultModeGradient">
          <h3 className="csConsultModeTitle">{staticText.consultModeTitle}</h3>
          <div className="csConsultModeRow">
            {CONSULT_TYPES.map(ct => {
              const isSelected = consultMode === ct.id;
              return (
                <div
                  key={ct.id}
                  className={'csMode' + (isSelected ? ' csModeSelected' : '')}
                  onClick={() => this.handleConsultMode(ct.id)}
                >
                  <img
                    src={isSelected ? ct.iconSelected : ct.iconDisabled}
                    alt={ct.name}
                    className="csModeIcon"
                    width={32}
                    height={32}
                  />
                  <div className="csModeTextWrapper">
                    <span className={'csModeText' + (isSelected ? ' csModeTextGreen' : '')}>{ct.name}</span>
                  </div>
                  <div className={'csRadio' + (isSelected ? ' csRadioChecked' : '')} />
                </div>
              );
            })}
          </div>
        </div>
      </div>
    );
  }

  renderPrivacyPolicy() {
    return (
      <p className="csPrivacyText">
        By proceeding, you agree to receive the consultation through PharmEasy&apos;s partner,
        MeraDoc. You consent to share your information with MeraDoc and agree to its{' '}
        <a
          href="https://meradoc.com/auth/privacy-policy?TYPE=PRIVACY_POLICY"
          className="csPrivacyLink"
          target="_blank"
          rel="noopener noreferrer"
        >
          {staticText.privacyPolicyLink}
        </a>
      </p>
    );
  }

  render() {
    const { orderLoading, orderSuccess, orderId, orderError } = this.state;
    const { selectedDoctor, selectedSlot, selectedDate, bifurcationData, patientDetails } = this.props.docConsult;

    return (
      <div className="csPageWrapper">
        {orderSuccess && selectedDoctor && (
          <DocConsultSuccessModal
            orderId={String(orderId)}
            patientName={patientDetails.name}
            doctor={selectedDoctor}
            slot={selectedSlot}
            date={selectedDate}
            bifurcationData={bifurcationData}
            consultationFees={selectedDoctor.consultationFees}
            onServeNext={() => { this.props.actions.cancelOrder(); browserHistory.push('/customer-info'); }}
          />
        )}

        <div className="consultSummaryContainer">
          <div className="consultSummaryInner">
            {this.renderSlotSection()}
            {this.renderConsultMode()}
            <PatientForm
              ref={(ref) => { this.patientFormRef = ref; }}
              patient={patientDetails}
              onChange={this.handlePatientChange}
            />
            <UploadReport ref={(ref) => { this.uploadReportRef = ref; }} />
            {orderError && (
              <p className="csOrderError">{orderError}</p>
            )}
            {this.renderPrivacyPolicy()}
          </div>
        </div>

        <FooterButtons
          backBtnText={staticText.backBtn}
          backBtnClick={() => browserHistory.push('/doc-consult/select-doctor')}
          proceedBtnText={orderLoading ? staticText.placingOrder : staticText.confirmBooking}
          proceedBtnClick={this.handleProceed}
          nextStateAllowed={this.isValid() && !orderLoading}
          cancelBtnText={staticText.cancelBtn}
          cancelBtnClick={() => browserHistory.push({ pathname: '/customer-info', state: { alreadyVisitedCustomerInfoPage: true } })}
        />
      </div>
    );
  }
}

SlotPatientPage.propTypes = {
  docConsult: PropTypes.object.isRequired,
  customer: PropTypes.object.isRequired,
  actions: PropTypes.object.isRequired,
};

export default connect(
  state => ({ docConsult: state.docConsult, customer: state.customer }),
  dispatch => ({ actions: bindActionCreators(actions, dispatch) })
)(SlotPatientPage);
